import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import unifiedApi from '../services/unifiedApi';
import { useFeatureFlags } from './useFeatureFlags.jsx';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import { FORM_LIMITS } from '../config/constants';

const STOP_ADDRESS_MIN_LENGTH = FORM_LIMITS.STOP_ADDRESS_MIN_LENGTH;

export function readCoord(value) {
  if (value === null || value === undefined || value === '') return NaN;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const n = parseFloat(String(value).trim().replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
}

const STOP_COORD_DECIMAL_PLACES = 8;

export function formatManualCoord(n) {
  if (!Number.isFinite(n)) return '';
  const s = n.toFixed(STOP_COORD_DECIMAL_PLACES);
  const trimmed = s.replace(/\.?0+$/, '');
  return trimmed === '' ? '0' : trimmed;
}

function studentToUpdateBody(s, assignedStopId) {
  return {
    first_name: s.firstName,
    last_name: s.lastName,
    date_of_birth: s.dateOfBirth,
    gender: s.gender || 'male',
    grade_level: s.grade_level || '',
    class_name: s.class_name ?? '',
    status: s.status || 'active',
    pickup_address: s.pickup_address || '',
    pickup_lat: s.pickup_latitude,
    pickup_lng: s.pickup_longitude,
    dropoff_address: s.dropoff_address ?? s.pickup_address ?? '',
    dropoff_lat: s.dropoff_latitude,
    dropoff_lng: s.dropoff_longitude,
    assigned_stop_id: assignedStopId,
  };
}

function assignedStopDisplayName(stop) {
  if (!stop) return '';
  return stop.stop_name || stop.name || '';
}

export const useStopWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { stopId } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(stopId);
  const stopFromNavigation = location.state?.stop;
  const { isTabEnabled } = useFeatureFlags();

  const [activeRoutesCount, setActiveRoutesCount] = useState(stopFromNavigation?.active_routes_count ?? 0);
  const isMapStepLocked = isEdit && activeRoutesCount > 0;

  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [schoolGeo, setSchoolGeo] = useState(null);
  const [schoolMeta, setSchoolMeta] = useState({ id: 'school', name: 'École' });

  const [pinLat, setPinLat] = useState(null);
  const [pinLng, setPinLng] = useState(null);
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');

  // Point retour (evening/outbound) — optionnel, voir CLAUDE.md "Arrêt avec
  // point de ramassage matin ≠ point de dépose soir".
  const [hasReturnPoint, setHasReturnPoint] = useState(false);
  const [returnPinLat, setReturnPinLat] = useState(null);
  const [returnPinLng, setReturnPinLng] = useState(null);
  const [manualReturnLat, setManualReturnLat] = useState('');
  const [manualReturnLng, setManualReturnLng] = useState('');
  // Quel point la carte (clic) et la géolocalisation modifient actuellement —
  // 'main' ou 'return'. Un seul jeu de listeners carte (voir handleMapClick),
  // pas de re-branchement à chaque bascule.
  const [activePinTarget, setActivePinTarget] = useState('main');

  const [stopName, setStopName] = useState('');
  const [stopStatus, setStopStatus] = useState('active');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [initialStudentIds, setInitialStudentIds] = useState([]);

  const studentIdsSnapshotRef = useRef([]);

  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const isLocatingRef = useRef(false);

  const [showRecap, setShowRecap] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingStop, setLoadingStop] = useState(false);

  // Refs exposed to the view for Mapbox map synchronisation
  const pinRef = useRef({ lat: null, lng: null });
  const returnPinRef = useRef({ lat: null, lng: null });
  const schoolGeoRef = useRef(schoolGeo);
  const schoolMetaRef = useRef(schoolMeta);
  const activePinTargetRef = useRef('main');

  useEffect(() => {
    pinRef.current = { lat: pinLat, lng: pinLng };
  }, [pinLat, pinLng]);
  useEffect(() => {
    returnPinRef.current = { lat: returnPinLat, lng: returnPinLng };
  }, [returnPinLat, returnPinLng]);
  useEffect(() => {
    schoolGeoRef.current = schoolGeo;
  }, [schoolGeo]);
  useEffect(() => {
    schoolMetaRef.current = schoolMeta;
  }, [schoolMeta]);
  useEffect(() => {
    activePinTargetRef.current = activePinTarget;
  }, [activePinTarget]);

  const featureFlags = useMemo(
    () => ({
      type: isTabEnabled('stops', 'type'),
      avance: isTabEnabled('stops', 'avance'),
    }),
    [isTabEnabled],
  );

  // setPin updates both state and ref immediately so map effects see the latest pin.
  const setPin = useCallback((lat, lng) => {
    const la = readCoord(lat);
    const ln = readCoord(lng);
    if (Number.isNaN(la) || Number.isNaN(ln) || la < -90 || la > 90 || ln < -180 || ln > 180) {
      return false;
    }
    pinRef.current = { lat: la, lng: ln };
    setPinLat(la);
    setPinLng(ln);
    setManualLat(formatManualCoord(la));
    setManualLng(formatManualCoord(ln));
    return true;
  }, []);

  // setReturnPin mirrors setPin but for the optional return-direction coordinates —
  // pas de marqueur carte pour cette première itération, saisie manuelle uniquement.
  const setReturnPin = useCallback((lat, lng) => {
    const la = readCoord(lat);
    const ln = readCoord(lng);
    if (Number.isNaN(la) || Number.isNaN(ln) || la < -90 || la > 90 || ln < -180 || ln > 180) {
      return false;
    }
    setReturnPinLat(la);
    setReturnPinLng(ln);
    setManualReturnLat(formatManualCoord(la));
    setManualReturnLng(formatManualCoord(ln));
    return true;
  }, []);

  const toggleReturnPoint = useCallback((checked) => {
    setHasReturnPoint(checked);
    if (checked) {
      // Bascule immédiatement la cible carte/géoloc sur le point retour — l'admin
      // vient de cocher la case, l'action suivante la plus probable est de le placer.
      setActivePinTarget('return');
    } else {
      setActivePinTarget('main');
      setReturnPinLat(null);
      setReturnPinLng(null);
      setManualReturnLat('');
      setManualReturnLng('');
    }
  }, []);

  // Cible unique des clics carte / géolocalisation, lue depuis un ref pour ne
  // jamais re-brancher le listener carte quand activePinTarget change.
  const handleMapClick = useCallback(
    (lat, lng) => {
      if (activePinTargetRef.current === 'return') {
        setReturnPin(lat, lng);
      } else {
        setPin(lat, lng);
      }
    },
    [setPin, setReturnPin],
  );

  // Load current school for map marker
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await unifiedApi.schools.getCurrent();
        if (cancelled || !res?.success || !res.data) return;
        const d = res.data;
        const lat = readCoord(d.latitude ?? d.lat);
        const lng = readCoord(d.longitude ?? d.lng);
        setSchoolMeta({
          id: d.id || 'school',
          name: (d.name && String(d.name).trim()) || 'École',
        });
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setSchoolGeo({ lat, lng });
        } else {
          setSchoolGeo(null);
        }
      } catch (e) {
        if (!cancelled) setSchoolGeo(null);
        logger.warn("Impossible de charger l'école courante pour la carte", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadStudents = useCallback(async () => {
    setStudentsLoading(true);
    try {
      const res = await unifiedApi.students.getAllUnlimited();
      if (res.success && Array.isArray(res.data)) {
        setStudents(res.data);
      } else {
        setStudents([]);
      }
    } catch (e) {
      logger.error(e);
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  // Load stop data for edit mode
  useEffect(() => {
    if (!isEdit || !stopId) return;
    let cancelled = false;
    const hasNavigationStop = stopFromNavigation && String(stopFromNavigation.id) === String(stopId);

    if (hasNavigationStop) {
      const st = stopFromNavigation;
      setStopName(st.stop_name || st.name || '');
      setStopStatus(st.status || 'active');
      setAddress(typeof st.address === 'string' ? st.address : '');
      setPostalCode(st.postal_code || '');
      setCity(st.city || '');
      setDistrict(st.district || '');
      setPickupTime(st.pickup_time ? String(st.pickup_time).slice(0, 5) : '');
      setDropoffTime(st.dropoff_time ? String(st.dropoff_time).slice(0, 5) : '');
      const lat = readCoord(st.latitude ?? st.lat);
      const lng = readCoord(st.longitude ?? st.lng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) setPin(lat, lng);
      const rLat = readCoord(st.return_latitude);
      const rLng = readCoord(st.return_longitude);
      if (!Number.isNaN(rLat) && !Number.isNaN(rLng)) {
        setHasReturnPoint(true);
        setReturnPin(rLat, rLng);
      }
    }

    (async () => {
      setLoadingStop(!hasNavigationStop);
      try {
        const res = await unifiedApi.stops.getById(stopId);
        if (cancelled || !res.success || !res.data) {
          if (!hasNavigationStop) {
            toast.error('Arrêt introuvable');
            navigate('/admin/stops');
          }
          return;
        }
        const st = res.data;
        setStopName(st.stop_name || st.name || '');
        setStopStatus(st.status || 'active');
        setAddress(typeof st.address === 'string' ? st.address : '');
        setPostalCode(st.postal_code || '');
        setCity(st.city || '');
        setDistrict(st.district || '');
        setPickupTime(st.pickup_time ? String(st.pickup_time).slice(0, 5) : '');
        setDropoffTime(st.dropoff_time ? String(st.dropoff_time).slice(0, 5) : '');
        setActiveRoutesCount(st.active_routes_count ?? 0);
        const lat = readCoord(st.latitude ?? st.lat);
        const lng = readCoord(st.longitude ?? st.lng);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) setPin(lat, lng);
        const rLat = readCoord(st.return_latitude);
        const rLng = readCoord(st.return_longitude);
        if (!Number.isNaN(rLat) && !Number.isNaN(rLng)) {
          setHasReturnPoint(true);
          setReturnPin(rLat, rLng);
        }
      } catch (e) {
        logger.error(e);
        if (!hasNavigationStop) {
          toast.error('Erreur chargement arrêt');
          navigate('/admin/stops');
        }
      } finally {
        if (!cancelled) setLoadingStop(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, stopId, navigate, stopFromNavigation, setPin, setReturnPin]);

  // Pre-select students already at this stop
  useEffect(() => {
    if (!isEdit || !stopId || students.length === 0) return;
    const atStop = students.filter((s) => s.assigned_stop_id === stopId).map((s) => s.id);
    setInitialStudentIds(atStop);
    setSelectedStudentIds(atStop);
  }, [isEdit, stopId, students]);

  const validateStep2 = () => {
    if (!stopName.trim()) {
      toast.error("Le nom de l'arrêt est obligatoire.");
      return false;
    }
    if (address.trim().length < STOP_ADDRESS_MIN_LENGTH) {
      toast.error(`L'adresse complète est obligatoire (${STOP_ADDRESS_MIN_LENGTH} caractères minimum).`);
      return false;
    }
    return true;
  };

  const validateAll = () => {
    if (pinLat == null || pinLng == null) {
      toast.error('Sélectionnez un point sur la carte ou saisissez latitude et longitude.');
      return false;
    }
    if (hasReturnPoint && (returnPinLat == null || returnPinLng == null)) {
      toast.error('Saisissez les coordonnées du point retour, ou décochez la case.');
      return false;
    }
    return validateStep2();
  };

  const applyManualCoords = () => {
    if (isMapStepLocked) return;
    const lat = readCoord(manualLat);
    const lng = readCoord(manualLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error('Latitude et longitude invalides.');
      return;
    }
    if (!setPin(lat, lng)) {
      toast.error('Coordonnées hors plage valide.');
    }
  };

  const applyManualReturnCoords = () => {
    const lat = readCoord(manualReturnLat);
    const lng = readCoord(manualReturnLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error('Latitude et longitude (retour) invalides.');
      return;
    }
    if (!setReturnPin(lat, lng)) {
      toast.error('Coordonnées (retour) hors plage valide.');
    }
  };

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Save snapshot on open so Annuler can revert to it
  const openStudentsModal = useCallback(() => {
    studentIdsSnapshotRef.current = [...selectedStudentIds];
    setShowStudentsModal(true);
  }, [selectedStudentIds]);

  const cancelStudentsModal = useCallback(() => {
    setSelectedStudentIds(studentIdsSnapshotRef.current);
    setStudentSearch('');
    setShowStudentsModal(false);
  }, []);

  const confirmStudentsModal = useCallback(() => {
    setStudentSearch('');
    setShowStudentsModal(false);
  }, []);

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => {
      const name = `${s.firstName || ''} ${s.lastName || ''}`.toLowerCase();
      return name.includes(q) || (s.ref || '').toLowerCase().includes(q);
    });
  }, [students, studentSearch]);

  const studentOptionLabel = (s) => {
    const base = `${s.firstName || ''} ${s.lastName || ''}`.trim();
    const sn = assignedStopDisplayName(s.assignedStop);
    return sn ? `${base} — ${sn}` : base;
  };

  const addedIds = selectedStudentIds.filter((id) => !initialStudentIds.includes(id));
  const removedIds = initialStudentIds.filter((id) => !selectedStudentIds.includes(id));

  const buildStopPayload = () => ({
    stop_name: stopName.trim(),
    address: address.trim(),
    latitude: pinLat,
    longitude: pinLng,
    city: city.trim() || null,
    postal_code: postalCode.trim() || null,
    district: district.trim() || null,
    pickup_time: pickupTime && String(pickupTime).trim() ? String(pickupTime).trim().slice(0, 5) : null,
    dropoff_time: dropoffTime && String(dropoffTime).trim() ? String(dropoffTime).trim().slice(0, 5) : null,
    return_latitude: hasReturnPoint ? returnPinLat : null,
    return_longitude: hasReturnPoint ? returnPinLng : null,
    // En édition, préserver le statut existant (ex: 'pending' proposé par un chauffeur) —
    // ne jamais l'écraser à 'active' au passage dans l'éditeur, sinon ça court-circuite
    // silencieusement la validation admin (Valider/Rejeter).
    status: isEdit ? stopStatus : 'active',
  });

  const requestAdminLocation = useCallback(() => {
    if (isLocatingRef.current) return;

    if (!navigator?.geolocation) {
      setLocationError("La géolocalisation n'est pas supportée par ce navigateur.");
      return;
    }

    isLocatingRef.current = true;
    setIsLocating(true);
    setLocationError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (activePinTargetRef.current === 'return') {
          setReturnPin(position.coords.latitude, position.coords.longitude);
        } else {
          setPin(position.coords.latitude, position.coords.longitude);
        }
        setIsLocating(false);
        isLocatingRef.current = false;
      },
      (err) => {
        const messages = {
          1: 'Permission refusée. Autorisez la géolocalisation dans votre navigateur.',
          2: 'Position GPS indisponible.',
          3: 'Délai dépassé pour obtenir la position.',
        };
        setLocationError(messages[err.code] || 'Impossible de récupérer la position.');
        setIsLocating(false);
        isLocatingRef.current = false;
      },
      { timeout: 10000, maximumAge: 0 },
    );
  }, [setPin, setReturnPin]);

  const handleCopySelectedCoordinates = async () => {
    if (pinLat == null || pinLng == null) return;
    const text = `${formatManualCoord(pinLat)}, ${formatManualCoord(pinLng)}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      toast.success('Coordonnees copiees.');
    } catch (e) {
      logger.error('Erreur copie coordonnees:', e);
      toast.error('Impossible de copier les coordonnees.');
    }
  };

  const runSubmit = async () => {
    if (!validateStep2() || pinLat == null || pinLng == null) {
      toast.error('Données incomplètes.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildStopPayload();
      let finalStopId = stopId;

      if (isEdit) {
        const upd = await unifiedApi.stops.update(stopId, payload, featureFlags);
        if (upd.success === false) throw new Error(upd.error || upd.message || 'Mise à jour échouée');
      } else {
        const created = await unifiedApi.stops.create(payload, featureFlags);
        if (created.success === false || !created.data?.id) {
          throw new Error(created.error || created.message || 'Création échouée');
        }
        finalStopId = created.data.id;
      }

      const byId = Object.fromEntries(students.map((s) => [s.id, s]));
      const toAssign = isEdit ? addedIds : selectedStudentIds;
      for (const sid of toAssign) {
        const s = byId[sid];
        if (!s) continue;
        const r = await unifiedApi.students.update(sid, studentToUpdateBody(s, finalStopId));
        if (r.success === false) throw new Error(r.error || `Élève ${sid}`);
      }

      if (isEdit) {
        for (const sid of removedIds) {
          const s = byId[sid];
          if (!s) continue;
          const r = await unifiedApi.students.update(sid, studentToUpdateBody(s, null));
          if (r.success === false) throw new Error(r.error || `Désassignation élève ${sid}`);
        }
      }

      toast.success(isEdit ? 'Arrêt mis à jour.' : 'Arrêt créé et élèves assignés.');
      setShowRecap(false);
      queryClient.invalidateQueries({ queryKey: ['stops'] });
      navigate('/admin/stops');
    } catch (e) {
      logger.error(e);
      toast.error(e.message || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    // Navigation
    navigate,
    isEdit,
    stopId,
    isMapStepLocked,
    featureFlags,
    // Students modal
    showStudentsModal,
    setShowStudentsModal,
    // School geo (for map marker)
    schoolGeo,
    setSchoolGeo,
    schoolMeta,
    setSchoolMeta,
    schoolGeoRef,
    schoolMetaRef,
    // Pin
    pinLat,
    pinLng,
    isLocating,
    locationError,
    requestAdminLocation,
    manualLat,
    setManualLat,
    manualLng,
    setManualLng,
    pinRef,
    setPin,
    handleMapClick,
    // Point retour (optionnel)
    hasReturnPoint,
    toggleReturnPoint,
    returnPinLat,
    returnPinLng,
    returnPinRef,
    manualReturnLat,
    setManualReturnLat,
    manualReturnLng,
    setManualReturnLng,
    applyManualReturnCoords,
    activePinTarget,
    setActivePinTarget,
    // Form fields
    stopName,
    setStopName,
    address,
    setAddress,
    postalCode,
    setPostalCode,
    city,
    setCity,
    district,
    setDistrict,
    pickupTime,
    setPickupTime,
    dropoffTime,
    setDropoffTime,
    // Students
    students,
    studentsLoading,
    studentSearch,
    setStudentSearch,
    selectedStudentIds,
    initialStudentIds,
    filteredStudents,
    studentOptionLabel,
    addedIds,
    removedIds,
    toggleStudent,
    // Modal / loading
    showRecap,
    setShowRecap,
    submitting,
    loadingStop,
    // Actions
    applyManualCoords,
    handleCopySelectedCoordinates,
    runSubmit,
    validateStep2,
    validateAll,
    openStudentsModal,
    cancelStudentsModal,
    confirmStudentsModal,
  };
};

export default useStopWizard;
